import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  NewReleases,
  PlayArrow,
  QuestionAnswer
} from '@mui/icons-material';
import CampaignCard from '../components/CampaignCard';
import { 
  dummyCampaigns, 
  getPopularCampaigns, 
  getNewCampaigns, 
  getOngoingCampaigns 
} from '../utils/dummyData';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const popularCampaigns = getPopularCampaigns();
  const newCampaigns = getNewCampaigns();
  const ongoingCampaigns = getOngoingCampaigns();

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      {/* 히어로 섹션 */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        <Paper
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            스마트리뷰
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            오픈리뷰 체험 후 무료 서비스입니다.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            다양한 상품과 플레이스를 체험하고 솔직한 리뷰를 작성해보세요. 
            리뷰 작성 시 포인트와 다양한 혜택을 드립니다!
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: 'white',
              color: '#667eea',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)'
              }
            }}
          >
            지금 시작하기
          </Button>
        </Paper>
      </Box>

      {/* 공고주 이신가요? 섹션 */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        <Paper
          sx={{
            background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
            color: 'white',
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              공고주 이신가요?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              오픈리뷰는 체험단 무료 서비스입니다.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QuestionAnswer sx={{ fontSize: 60, opacity: 0.8, mr: 2 }} />
            <Button
              variant="outlined"
              sx={{
                color: 'white',
                borderColor: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white'
                }
              }}
            >
              자세히 보기
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* 인기 캠페인 섹션 */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <TrendingUp sx={{ color: '#f44336', mr: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            인기 캠페인
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)' 
          },
          gap: { xs: 2, sm: 3 },
          px: { xs: 1, sm: 0 }
        }}>
          {popularCampaigns.map((campaign) => (
            <CampaignCard 
              key={campaign.id} 
              campaign={campaign} 
              onClick={() => handleCampaignClick(campaign.id)}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: { xs: 3, sm: 4 } }} />

      {/* 신규 캠페인 섹션 */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <NewReleases sx={{ color: '#4caf50', mr: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            신규 캠페인
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)' 
          },
          gap: { xs: 2, sm: 3 },
          px: { xs: 1, sm: 0 }
        }}>
          {newCampaigns.map((campaign) => (
            <CampaignCard 
              key={campaign.id} 
              campaign={campaign} 
              onClick={() => handleCampaignClick(campaign.id)}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: { xs: 3, sm: 4 } }} />

      {/* 진행중인 캠페인 섹션 */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <PlayArrow sx={{ color: '#2196f3', mr: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            진행중인 캠페인
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)' 
          },
          gap: { xs: 2, sm: 3 },
          px: { xs: 1, sm: 0 }
        }}>
          {ongoingCampaigns.map((campaign) => (
            <CampaignCard 
              key={campaign.id} 
              campaign={campaign} 
              onClick={() => handleCampaignClick(campaign.id)}
            />
          ))}
        </Box>
      </Box>

      {/* 통계 섹션 */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            스마트리뷰 현황
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: 'repeat(2, 1fr)', 
              sm: 'repeat(4, 1fr)' 
            },
            gap: { xs: 2, sm: 4 },
            mt: 2
          }}>
            <Box>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {dummyCampaigns.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                진행중인 캠페인
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="primary" fontWeight="bold">
                1,234
              </Typography>
              <Typography variant="body2" color="text.secondary">
                참여 회원수
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="primary" fontWeight="bold">
                5,678
              </Typography>
              <Typography variant="body2" color="text.secondary">
                작성된 리뷰
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="primary" fontWeight="bold">
                98%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                만족도
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* 카테고리 섹션 */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          카테고리별 캠페인
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: 'repeat(2, 1fr)', 
            sm: 'repeat(3, 1fr)', 
            md: 'repeat(6, 1fr)' 
          },
          gap: { xs: 1, sm: 2 }
        }}>
          {[
            { name: '뷰티', icon: '💄', color: '#FF6B9D', count: 45 },
            { name: '음식', icon: '🍕', color: '#4ECDC4', count: 32 },
            { name: '맛집', icon: '🍽️', color: '#45B7D1', count: 28 },
            { name: '전자제품', icon: '📱', color: '#96CEB4', count: 19 },
            { name: '패션', icon: '👗', color: '#FECA57', count: 15 },
            { name: '여행', icon: '✈️', color: '#FF9FF3', count: 12 }
          ].map((category) => (
            <Card 
              key={category.name}
              sx={{ 
                textAlign: 'center', 
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent>
                <Typography variant="h3" sx={{ mb: 1 }}>
                  {category.icon}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {category.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {category.count}개
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HomePage; 